import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Loader2, Save, Trash2, Upload, X, ExternalLink, Hash, QrCode } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ShopNav } from "../components/shop/ShopNav";
import { ProductImage } from "../components/shop/ProductCard";
import { StockLog } from "../components/shop/StockLog";
import { LabelPrintDialog } from "../components/shop/LabelPrintDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { api, errorMessage } from "../lib/api";
import { nok, productUrl } from "../lib/shop";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const EMPTY = { name: "", description: "", price: "", image_url: "", stock: "10", low_stock_threshold: "3", active: true, sku: "", vat_rate: "25" };
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Nedskalerer store bilete i nettlesaren (same tilnærming som arrangementsbilete)
const compressImage = (file, maxDim = 1920, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("komprimering feilet"))), "image/jpeg", quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("kunne ikke lese bildet"));
    };
    img.src = url;
  });

export default function ShopProductEditorPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const { organizer } = useAuth();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ordersWithProduct, setOrdersWithProduct] = useState(0);
  const [skuError, setSkuError] = useState(null); // { sku, name } når backend melder duplikat (409 sku_taken)
  const [suggestingSku, setSuggestingSku] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [saved, setSaved] = useState(null); // vara slik ho er lagra (til etikett-dialogen)
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    api.get(`/my/shop/products/${id}`)
      .then((res) => {
        const p = res.data.product;
        setSaved(p);
        setForm({ name: p.name, description: p.description || "", price: String(p.price), image_url: p.image_url || "", stock: String(p.stock), low_stock_threshold: String(p.low_stock_threshold ?? 3), active: !!p.active, sku: p.sku || "", vat_rate: String(p.vat_rate ?? 25) });
        setOrdersWithProduct(res.data.orders_with_product || 0);
      })
      .catch((err) => {
        toast.error(errorMessage(err, t("Fant ikke varen")));
        navigate("/dashboard/butikk");
      })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate, t]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setSku = (value) => {
    setSkuError(null);
    set("sku", value);
  };
  const priceNum = Number(form.price);
  const canSave = form.name.trim().length >= 2 && form.price !== "" && Number.isFinite(priceNum) && priceNum >= 0;

  // Hentar neste ledige varenummer (V-0001, V-0002 …) frå backend og fyller feltet
  const suggestSku = async () => {
    setSuggestingSku(true);
    try {
      const { data } = await api.get("/my/shop/products/next-sku");
      setSku(data.sku || "");
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke foreslå varenummer")));
    } finally {
      setSuggestingSku(false);
    }
  };

  const uploadImageFile = async (file) => {
    let payload = file;
    if (file.size > MAX_IMAGE_BYTES) {
      try {
        const blob = await compressImage(file);
        if (blob.size > MAX_IMAGE_BYTES) throw new Error("for stort");
        payload = new File([blob], (file.name.replace(/\.[^.]+$/, "") || "bilde") + ".jpg", { type: "image/jpeg" });
      } catch {
        toast.error(t("Kunne ikke laste opp bildet"));
        return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", payload);
      const { data } = await api.post("/my/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success(t("Bildet er lastet opp"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke laste opp bildet")));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toast.error(t("Fyll inn navn og pris"));
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(), description: form.description.trim(), price: Math.round(priceNum), image_url: form.image_url, stock: Math.max(0, parseInt(form.stock, 10) || 0),
      low_stock_threshold: Math.max(0, parseInt(form.low_stock_threshold, 10) || 0), active: form.active, sku: form.sku.trim(), vat_rate: parseInt(form.vat_rate, 10),
    };
    try {
      let res;
      if (isNew) {
        res = await api.post("/my/shop/products", payload);
        toast.success(res.data?.sku_assigned && res.data?.product?.sku ? t("Varen er opprettet med varenummer {sku}", { sku: res.data.product.sku }) : t("Varen er opprettet"));
      } else {
        res = await api.put(`/my/shop/products/${id}`, payload);
        toast.success(res.data?.sku_assigned && res.data?.product?.sku ? t("Varen er lagret med varenummer {sku}", { sku: res.data.product.sku }) : t("Varen er lagret"));
      }
      navigate("/dashboard/butikk");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "sku_taken") {
        setSkuError({ sku: detail.sku, name: detail.product_name || "" });
        toast.error(t("Varenummer {sku} er allerede i bruk på «{name}»", { sku: detail.sku, name: detail.product_name || "" }));
        document.getElementById("pe-sku")?.focus();
      } else {
        toast.error(errorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/my/shop/products/${id}`);
      toast.success(t("Varen er slettet"));
      navigate("/dashboard/butikk");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const preview = { name: form.name || t("Navn på varen"), price: Math.round(priceNum) || 0, image_url: form.image_url, stock_status: (parseInt(form.stock, 10) || 0) === 0 ? "sold_out" : (parseInt(form.low_stock_threshold, 10) || 0) > 0 && (parseInt(form.stock, 10) || 0) <= (parseInt(form.low_stock_threshold, 10) || 0) ? "low" : "in_stock" };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="product-editor-page">
        <ShopNav />
        <div className="flex items-center justify-between gap-3">
          <Button asChild variant="ghost" size="sm" data-testid="product-editor-back"><Link to="/dashboard/butikk"><ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Tilbake")}</Link></Button>
          {!isNew && organizer?.id && (
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" onClick={() => setPrintOpen(true)} disabled={!saved} data-testid="product-editor-labels-button"><QrCode className="w-4 h-4 mr-1.5" /> {t("Strekkode / etikett")}</Button>
              <Button asChild variant="ghost" size="sm" data-testid="product-editor-view-public"><a href={productUrl(organizer.id, id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" /> {t("Se vare")}</a></Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid gap-6 lg:grid-cols-12"><Skeleton className="h-[520px] rounded-xl lg:col-span-7" /><Skeleton className="h-80 rounded-xl lg:col-span-5" /></div>
        ) : (
          <form onSubmit={save} className="grid gap-6 lg:grid-cols-12" data-testid="product-editor-form">
            <Card className="border-border lg:col-span-7">
              <CardContent className="p-5 sm:p-6 space-y-6">
                <h2 className="text-lg font-heading font-semibold tracking-tight">{isNew ? t("Ny vare") : t("Rediger vare")}</h2>
                <div className="space-y-1.5">
                  <Label htmlFor="pe-name">{t("Navn på varen")}</Label>
                  <Input id="pe-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required data-testid="product-editor-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pe-desc">{t("Beskrivelse")}</Label>
                  <Textarea id="pe-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} maxLength={4000} placeholder={t("Fortell kort om varen – format, størrelse, materiale, hvem den passer for.")} data-testid="product-editor-description-textarea" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="pe-price">{t("Pris (kr)")}</Label>
                    <Input id="pe-price" type="number" min={0} max={100000} inputMode="numeric" value={form.price} onChange={(e) => set("price", e.target.value)} required data-testid="product-editor-price-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pe-sku">{t("Varenummer (valgfritt)")}</Label>
                    <div className="flex gap-2">
                      <Input
                        id="pe-sku" value={form.sku} onChange={(e) => setSku(e.target.value)} maxLength={40} placeholder={t("Tildeles automatisk")}
                        aria-invalid={!!skuError} aria-describedby="pe-sku-help"
                        className={`font-mono ${skuError ? "border-[hsl(var(--danger))] focus-visible:ring-[hsl(var(--danger))]" : ""}`}
                        data-testid="product-editor-sku-input"
                      />
                      <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={suggestSku} disabled={suggestingSku} title={t("Foreslå neste ledige varenummer")} aria-label={t("Foreslå neste ledige varenummer")} data-testid="product-editor-sku-suggest-button">
                        {suggestingSku ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
                      </Button>
                    </div>
                    {skuError ? (
                      <p id="pe-sku-help" role="alert" className="text-xs text-[hsl(var(--danger))]" data-testid="product-editor-sku-error">
                        {t("Varenummer {sku} er allerede i bruk på «{name}»", { sku: skuError.sku, name: skuError.name })}
                      </p>
                    ) : (
                      <p id="pe-sku-help" className="text-xs text-muted-foreground">{!isNew && saved?.sku ? t("Bokstaver og tall, må være unikt i butikken din. Tømmer du feltet, beholdes dagens nummer.") : t("Bokstaver og tall, må være unikt i butikken din. Tomt felt gir neste ledige nummer (V-0001, V-0002 …).")}</p>
                    )}
                  </div>
                </div>

                {/* Bilde */}
                <div className="space-y-2">
                  <Label>{t("Bilde")}</Label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full sm:w-56 aspect-[4/3] rounded-xl overflow-hidden bg-muted border border-border shrink-0">
                      {form.image_url ? <ProductImage product={{ image_url: form.image_url, name: form.name }} /> : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><ImagePlus className="w-8 h-8" /></div>}
                    </div>
                    <div className="space-y-2 text-sm">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImageFile(e.target.files[0])} data-testid="product-editor-image-upload" />
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="product-editor-image-button">
                          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} {uploading ? t("Laster opp …") : form.image_url ? t("Bytt bilde") : t("Last opp bilde")}
                        </Button>
                        {form.image_url && <Button type="button" variant="ghost" onClick={() => set("image_url", "")} data-testid="product-editor-image-remove"><X className="w-4 h-4 mr-1.5" /> {t("Fjern bilde")}</Button>}
                      </div>
                      <p className="text-xs text-muted-foreground">{t("JPG, PNG eller WEBP, maks 5 MB. Lys bakgrunn og hele varen i bildet fungerer best.")}</p>
                    </div>
                  </div>
                </div>

                {/* Lager */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="pe-stock">{t("Antall på lager")}</Label>
                    <Input id="pe-stock" type="number" min={0} max={100000} inputMode="numeric" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="product-editor-stock-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pe-low">{t("Varsle ved")}</Label>
                    <Input id="pe-low" type="number" min={0} max={1000} inputMode="numeric" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} data-testid="product-editor-low-stock-threshold-input" />
                    <p className="text-xs text-muted-foreground">{t("Du får e-post når lageret er lik eller lavere enn dette tallet. 0 = ingen varsel.")}</p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>{t("MVA-sats")}</Label>
                    <Select value={form.vat_rate} onValueChange={(v) => set("vat_rate", v)}>
                      <SelectTrigger data-testid="product-editor-vat-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["0", "12", "15", "25"].map((v) => <SelectItem key={v} value={v}>{v} %</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t("Brukes bare på fakturaen når du er MVA-registrert (se Innstillinger).")}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("Synlig i butikken")}</Label>
                    <label className="flex items-center gap-3 rounded-lg border border-border px-3 h-10 cursor-pointer">
                      <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-editor-active-switch" />
                      <span className="text-sm">{form.active ? t("Aktiv") : t("Skjult")}</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <Button type="submit" disabled={!canSave || saving} data-testid="product-editor-save-button">
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {saving ? t("Lagrer …") : t("Lagre")}
                  </Button>
                  {!isNew && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" variant="ghost" className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" data-testid="product-editor-delete-button"><Trash2 className="w-4 h-4 mr-1.5" /> {t("Slett")}</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("Slette varen?")}</AlertDialogTitle>
                          <AlertDialogDescription>{t("Varen fjernes fra butikken. Ordrer som allerede er lagt inn beholdes.")}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="product-editor-delete-cancel">{t("Avbryt")}</AlertDialogCancel>
                          <AlertDialogAction onClick={remove} className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" data-testid="product-editor-delete-confirm">{t("Ja, slett")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Førehandsvising */}
            <div className="lg:col-span-5 space-y-3 lg:sticky lg:top-24 self-start">
              <p className="text-sm font-medium text-muted-foreground">{t("Forhåndsvisning")}</p>
              <Card className="overflow-hidden border-border" data-testid="product-editor-preview">
                <div className="relative aspect-[4/3] bg-muted">
                  <ProductImage product={preview} />
                  {preview.stock_status !== "in_stock" && (
                    <div className="absolute top-3 right-3">
                      <Badge className={preview.stock_status === "sold_out" ? "bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))]" : "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]"}>{preview.stock_status === "sold_out" ? t("Utsolgt") : t("Få igjen")}</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-4 space-y-1">
                  <p className="font-heading font-semibold leading-snug">{preview.name}</p>
                  <p className="text-xs text-muted-foreground">{organizer?.org_name}</p>
                  <p className="font-heading font-semibold tabular-nums pt-1">{preview.price === 0 ? t("Gratis") : nok(preview.price)}</p>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">
                {t("Slik ser varen ut i butikken")}
                {!isNew && ordersWithProduct > 0 && (
                  <> · <Link to={`/dashboard/butikk/ordrer?product=${id}`} className="underline underline-offset-2 hover:text-foreground" data-testid="product-editor-orders-link">{t("{n} bestillinger med denne varen", { n: ordersWithProduct })}</Link></>
                )}
              </p>
            </div>
          </form>
        )}
        {/* Lagerhistorikk (berre eksisterande varer) – lastar på nytt når lageret i skjemaet er lagra */}
        {!isNew && !loading && <StockLog productId={id} />}
      </div>
      {saved && <LabelPrintDialog open={printOpen} onOpenChange={setPrintOpen} products={[saved]} />}
    </DashboardLayout>
  );
}
